import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Sparkles, Loader2, Building2, AlertTriangle, MoveRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import WizardFechamento from "../components/WizardFechamento";

const ESTAGIOS = [
  { id: "lead", label: "Lead", color: "#6b7280" },
  { id: "contato", label: "Contato", color: "#3b82f6" },
  { id: "visita", label: "Visita", color: "#0891b2" },
  { id: "proposta", label: "Levantamento de Proposta", color: "#7c3aed" },
  { id: "analise", label: "Análise", color: "#eab308" },
  { id: "contrato", label: "Contrato", color: "#F97316" },
  { id: "efetivado", label: "Pedido de Venda · Concluído", color: "#16a34a", isFinal: true },
];

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const diasDesde = (iso) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

export default function Funil() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [scoringId, setScoringId] = useState(null);
  const [wizardLead, setWizardLead] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const fetchLeads = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };

  useEffect(() => { fetchLeads(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    ESTAGIOS.forEach((e) => (g[e.id] = []));
    // Mapeamento de status legacy/inválidos para os 7 estágios oficiais
    const STATUS_MAP = {
      // Status null/vazio → primeira coluna "Lead"
      null: "lead",
      undefined: "lead",
      "": "lead",
      // Status de propostas/contratos finalizados → "efetivado"
      finalizada: "efetivado",
      finalizado: "efetivado",
      assinada: "efetivado",
      assinado: "efetivado",
      concluido: "efetivado",
      concluida: "efetivado",
      fechado: "efetivado",
      fechada: "efetivado",
      // Status legacy de envio → "contrato"
      enviada: "contrato",
      enviado: "contrato",
      // Sinônimos comuns
      negociacao: "analise",
      qualificacao: "contato",
      orcamento: "proposta",
    };
    leads.forEach((l) => {
      let s = l.status_funil;
      if (!g[s]) {
        // Tenta remapear; se ainda não encontrar, joga em "lead" como fallback (não some)
        s = STATUS_MAP[s] || "lead";
      }
      g[s].push({ ...l, _status_original: l.status_funil, _status_remapeado: s !== l.status_funil });
    });
    return g;
  }, [leads]);

  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData("text/plain", leadId);
    e.currentTarget.classList.add("opacity-50");
  };
  const onDragEnd = (e) => { e.currentTarget.classList.remove("opacity-50"); setDragOverCol(null); };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDragEnter = (colId) => setDragOverCol(colId);
  const onDragLeave = () => setDragOverCol(null);

  const onDrop = async (e, novoStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const leadId = e.dataTransfer.getData("text/plain");
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.status_funil === novoStatus) return;

    // Drop em "Pedido de Venda — Concluído" → abre wizard automaticamente
    const estagioFinal = ESTAGIOS.find((es) => es.id === novoStatus)?.isFinal;
    if (estagioFinal) {
      setWizardLead(lead);
      return;
    }

    // Atualização otimista
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, status_funil: novoStatus, movido_em: new Date().toISOString() } : l))
    );
    try {
      await api.patch(`/leads/${leadId}/status`, { status_funil: novoStatus });
      toast.success(`Movido para ${ESTAGIOS.find((s) => s.id === novoStatus)?.label}`);
    } catch (err) {
      toast.error("Falha ao mover lead");
      fetchLeads();
    }
  };

  const scoreLead = async (e, lead) => {
    e.stopPropagation();
    setScoringId(lead.id);
    try {
      const { data } = await api.post("/ai/score-lead", { lead_id: lead.id });
      setLeads((prev) =>
        prev.map((l) =>
          l.id === lead.id
            ? { ...l, score_ia: data.score, score_justificativa: data.fatores, score_proximo_passo: data.proximo_passo }
            : l
        )
      );
      toast.success(`Score IA: ${data.score}/100`);
    } catch {
      toast.error("Falha no scoring IA");
    } finally {
      setScoringId(null);
    }
  };

  const scoreColor = (s) => {
    if (s == null) return "#94a3b8";
    if (s >= 80) return "#16a34a";
    if (s >= 60) return "#F97316";
    if (s >= 40) return "#eab308";
    return "#dc2626";
  };

  const abrirLead = (lid) => navigate(`/app/leads/${lid}`);

  return (
    <div className="space-y-5" data-testid="funil-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Funil de Vendas
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Arraste os cards entre os 7 estágios. Cards parados <strong>há mais de 7 dias</strong> ficam destacados em amarelo.
            Arrastar para <strong>"Pedido de Venda · Concluído"</strong> abre o wizard de fechamento automaticamente.
          </p>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full">
          <MoveRight size={14} /> Role horizontalmente para ver todos os {ESTAGIOS.length} estágios
        </div>
      </div>

      <div className="overflow-x-auto pb-4 scroll-smooth" style={{ scrollbarWidth: "thin" }} data-testid="funil-scroll">
        <div
          className="grid gap-3 min-w-min"
          style={{ gridTemplateColumns: `repeat(${ESTAGIOS.length}, minmax(280px, 1fr))` }}
        >
          {ESTAGIOS.map((est) => {
            const isOver = dragOverCol === est.id;
            const isFinal = est.isFinal;
            return (
            <div
              key={est.id}
              data-testid={`funil-col-${est.id}`}
              onDragOver={onDragOver}
              onDragEnter={() => onDragEnter(est.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, est.id)}
              className={`rounded-lg p-2.5 border-2 transition-all ${isOver ? "border-dashed scale-[1.02]" : "border-solid"}`}
              style={{
                background: isFinal
                  ? (isOver ? "#D1FAE5" : "#ECFDF5")
                  : (isOver ? "#EFF6FF" : "#F8FAFC"),
                borderColor: isFinal ? "#16A34A" : (isOver ? "#3B82F6" : "#E2E8F0"),
              }}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: est.color }} />
                  <h3 className="font-semibold text-sm truncate" style={{ color: isFinal ? "#16A34A" : "#1B3A6B" }}>
                    {isFinal && <CheckCircle2 size={12} className="inline mr-1" />}
                    {est.label}
                  </h3>
                </div>
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  {grouped[est.id]?.length || 0}
                </Badge>
              </div>

              <div className="space-y-2 min-h-[80px]">
                {grouped[est.id]?.map((l) => {
                  const dias = diasDesde(l.movido_em || l.criado_em);
                  const parado = dias > 7 && !isFinal;
                  return (
                    <div
                      key={l.id}
                      data-testid={`lead-card-${l.id}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, l.id)}
                      onDragEnd={onDragEnd}
                      onClick={() => abrirLead(l.id)}
                      className={`bg-white rounded-md p-3 border-2 text-sm cursor-pointer hover:shadow-md transition-all ${parado ? "border-amber-400 bg-amber-50/50" : "border-slate-200"}`}
                    >
                      {parado && (
                        <div className="flex items-center gap-1 mb-2 text-xs font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-md">
                          <AlertTriangle size={12} /> Parado há {dias} dias — fazer follow-up
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold flex-1 min-w-0 truncate" style={{ color: "#1B3A6B" }}>
                          {l.nome}
                        </div>
                        <div
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{ background: `${scoreColor(l.score_ia)}1f`, color: scoreColor(l.score_ia) }}
                          title="Score IA"
                        >
                          {l.score_ia ?? "—"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2 truncate">
                        <Building2 size={11} className="flex-shrink-0" /> {l.empresa || "—"}
                      </div>
                      <div className="text-xs font-semibold" style={{ color: "#F97316" }}>
                        {fmt(l.valor_estimado)}
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed">
                        <span className={`text-[10px] ${parado ? "text-amber-700 font-bold" : "text-muted-foreground"}`}>
                          {dias}d {parado ? "parado!" : "no estágio"}
                        </span>
                        <button
                          data-testid={`score-btn-${l.id}`}
                          onClick={(e) => scoreLead(e, l)}
                          disabled={scoringId === l.id}
                          className="text-[10px] flex items-center gap-1 text-orange-600 hover:text-orange-700 disabled:opacity-50 font-semibold"
                        >
                          {scoringId === l.id ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                          Score IA
                        </button>
                      </div>
                    </div>
                  );
                })}
                {(grouped[est.id]?.length || 0) === 0 && (
                  <div className={`text-center text-xs py-8 rounded ${isOver ? "bg-blue-100 text-blue-700 font-bold" : "text-muted-foreground"}`}>
                    {isOver ? "↓ Solte aqui ↓" : "Vazio"}
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      </div>

      {/* Wizard auto-aberto quando arrasta para "Pedido de Venda · Concluído" */}
      {wizardLead && (
        <WizardFechamento
          lead={wizardLead}
          autoOpen
          onComplete={() => {
            setWizardLead(null);
            fetchLeads();
            toast.success("Pedido de venda concluído!");
          }}
          onClose={() => setWizardLead(null)}
        />
      )}
    </div>
  );
}
